import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, MapPin, Briefcase, DollarSign, 
  Video, FileText, Lock, CheckCircle, Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployerTalentSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccessPass, setHasAccessPass] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    location: 'all',
    work_type: 'all',
    has_video: false,
    has_resume: false
  });

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('keyword', search);
      if (filters.location !== 'all') params.append('location', filters.location);
      if (filters.work_type !== 'all') params.append('work_type', filters.work_type);
      if (filters.has_video) params.append('has_video', 'true');
      if (filters.has_resume) params.append('has_resume', 'true');
      
      const response = await axios.get(`${API}/portal/talent/search?${params}`);
      setCandidates(response.data.candidates || []);
      setHasAccessPass(response.data.has_access_pass || false);
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    if (search && !candidate.display_name?.toLowerCase().includes(search.toLowerCase()) &&
        !candidate.skills?.some(s => s.toLowerCase().includes(search.toLowerCase()))) {
      return false;
    }
    return true;
  });

  return (
    <PortalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Search Candidates</h1>
            <p className="text-muted-foreground">Find qualified professionals for your roles</p>
          </div>
          
          {!hasAccessPass && (
            <button
              onClick={() => navigate('/portal/talent/access')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
            >
              🔓 Unlock GoTalent Access
            </button>
          )}
        </div>

        {/* Access Pass Status */}
        {!hasAccessPass && (
          <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                    Limited Access
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Purchase a 30-day GoTalent Access Pass to view contact details, send offers, and invite candidates to interviews.
                    You can still browse profiles and send messages.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by skills, role..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="all">All Locations</option>
                <option value="Sydney">Sydney</option>
                <option value="Melbourne">Melbourne</option>
                <option value="Brisbane">Brisbane</option>
                <option value="Perth">Perth</option>
              </select>

              <select
                value={filters.work_type}
                onChange={(e) => setFilters({ ...filters, work_type: e.target.value })}
                className="px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="all">All Work Types</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contract">Contract</option>
              </select>
            </div>

            <div className="flex gap-4 mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.has_video}
                  onChange={(e) => setFilters({ ...filters, has_video: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Has Video</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.has_resume}
                  onChange={(e) => setFilters({ ...filters, has_resume: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Has Resume</span>
              </label>
              <button
                onClick={fetchCandidates}
                className="ml-auto px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Apply Filters
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Candidates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12">Loading candidates...</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No candidates found</h3>
              <p className="text-muted-foreground">Try adjusting your search filters</p>
            </div>
          ) : (
            filteredCandidates.map((candidate) => (
              <Card
                key={candidate.id}
                className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border hover:border-green-300 dark:hover:border-green-600"
                onClick={() => navigate(`/portal/talent/${candidate.user_id}`)}
              >
                <CardContent className="p-6 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg text-foreground">{candidate.display_name}</h3>
                      {candidate.discoverable && (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Open
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{candidate.location_suburb}, {candidate.location_state}</span>
                    </div>
                  </div>

                  {candidate.desired_roles && candidate.desired_roles.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Desired Roles</div>
                      <div className="text-sm text-muted-foreground line-clamp-2">
                        {candidate.desired_roles.join(', ')}
                      </div>
                    </div>
                  )}

                  {candidate.work_type_preferences && candidate.work_type_preferences.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {candidate.work_type_preferences.map((type) => (
                        <Badge key={type} className="text-xs capitalize">
                          {type.replace('_', '-')}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {candidate.skills && candidate.skills.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Skills</div>
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 5).map((skill) => (
                          <Badge key={skill} className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {candidate.skills.length > 5 && (
                          <Badge className="text-xs">+{candidate.skills.length - 5} more</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4 pt-3 border-t border-border text-xs text-muted-foreground">
                    {candidate.video_cover_letter_ref_id && (
                      <div className="flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        <span>Video</span>
                      </div>
                    )}
                    {candidate.resume_ref_id && (
                      <div className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <span>Resume</span>
                      </div>
                    )}
                    {!hasAccessPass && (
                      <div className="flex items-center gap-1 ml-auto text-yellow-600">
                        <Lock className="w-3 h-3" />
                        <span>Contact locked</span>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Updated {new Date(candidate.last_updated || candidate.updated_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </PortalLayout>
  );
};

export default EmployerTalentSearch;
